// @ts-strict-ignore
import { CopiedRangeOnLineArticleInformation } from '@project/components/article/model/copied-range-on-line-article-information';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { CoverLabelStripArticleInsertProcessor } from '@project/components/article/processor/prepare/insert/cover-label-strip-article-insert.processor';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { CopiedArticleInformation, CopiedLabelStripArticleInformation } from '@project/components/article/model/copied-article-information';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { IntRangeVO } from '../model/vo/property.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ConnectionAreaUsageVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { WireBridgeService } from '../manager/wire-bridge.service';
import { ProjectModel } from '@project/model/project.model';
import { IOperation } from '@shared/undo/operation/i-operation';
import { StaticPasteOperationHelper } from '@project/components/label/helper/paste/static-paste-operation.helper';
import { DynamicPasteOperationHelper } from '@project/components/label/helper/paste/dynamic-paste-operation.helper';
import { ReplaceLabelingArticleHelper } from '../../label/replace-labeling-helper/replace-labeling-article.helper';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({ providedIn: 'root' })
export class PasteArticleProcessor {
  private _staticPasteOperationHelper: StaticPasteOperationHelper;
  private _dynamicPasteOperationHelper: DynamicPasteOperationHelper;

  private _createdContainerGUIDs: string[] = [];
  private _dynamicArticleInfos: CopiedArticleInformation[] = [];

  private _moveArticlesMap: Dictionary<string, Vector3> = null;
  private _additionalUndoOperations: IOperation[] = [];

  constructor(
    private articleManager: ArticleManager,
    private wireBridgeService: WireBridgeService,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private projectMetaElementService: ProjectMetaElementService,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private containerService: ContainerService,
    private compositeArticleUtils: CompositeArticleUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private insertArticleUtils: InsertArticleUtils,
    private articleContainerLoader: ArticleContainerLoader,
    private articleContainerFactory: ArticleContainerFactory,
    private connectionInfoService: ConnectionInfoService,
    private detachArticleUsageService: DetachArticleUsageService,
    private articleUsageService: ArticleUsageService,
    private replaceBusbarHelper: ReplaceBusbarArticleHelper,
    private replaceBusbarService: ReplaceBusbarService
  ) {}

  public async pasteArticlesByRangeOnLineInfo(rangeOnLineInfo: CopiedRangeOnLineArticleInformation, rangeOnLineContainer: ArticleContainer,
    offset: number): Promise<string[] | null> {
    if (!rangeOnLineInfo || isNaN(offset) || !rangeOnLineContainer
      || rangeOnLineInfo.rangeOnLineArticleType !== rangeOnLineContainer.article.type.displayName) {
      return null;
    }
    try {
      const compositionContainer: CompositionContainer = this.containerService.getCompositionContainer(rangeOnLineContainer);
      const insertArticleHelper: InsertArticleHelper =
        this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(rangeOnLineContainer);
      if (ArticleUtils.isCoverArticle(rangeOnLineContainer.article)) {
        const coverInsertAvailable: boolean = CoverLabelStripArticleInsertProcessor.coverLabelStripInsertionAvailable(
          insertArticleHelper,
          rangeOnLineInfo.articlesInformation.length
        );
        if (!coverInsertAvailable) {
          await this.revertAdditionalUndoOperations();
          throw new Error('articleLocationProcessor.connect.message.error_cover_label_strip_limit');
        }
        this.updateOffsetPropertyForCoverLabelArticles(rangeOnLineInfo.articlesInformation, offset);
      }

      const commonLength: number = rangeOnLineInfo.commonArticlesLength;
      const commonLine: AdvancedLine = insertArticleHelper.calculateArticleLine(offset, commonLength);
      commonLine.overlapInfo = rangeOnLineInfo.overlapInfo;

      const busbarsToDetach: ArticleContainer[] = this.replaceBusbarService.getBusbarsToRecreateByInsertionPoint(
        commonLine.start,
        insertArticleHelper);
      this._moveArticlesMap = insertArticleHelper.calculateMoveOffsetByArticleLine(commonLine, busbarsToDetach.map((busbar) => busbar.articleGUID));
      const labelStripsToDetach: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByMoveMap(
        commonLine.start,
        this._moveArticlesMap,
        insertArticleHelper
      );

      if (this._moveArticlesMap && !CompositeArticleUtils.isCompositePartArticleExists(labelStripsToDetach) && !CompositeArticleUtils.isCompositePartArticleExists(busbarsToDetach)) {
        this._staticPasteOperationHelper = new StaticPasteOperationHelper(
          commonLine.start,
          compositionContainer,
          this.articleManager,
          this.projectModel,
          this.projectArticleService,
          this.articleContainerLoader,
          this.articleContainerFactory,
          this.detachArticleUsageService
        );
        await this.removeLabelingArticles(labelStripsToDetach);

        await this.replaceBusbarHelper.detachBusbarContainers(busbarsToDetach, this._moveArticlesMap, insertArticleHelper);
        const busbarsEndOverhangChildrenMoveMap: Dictionary<string, Vector3> = this.replaceBusbarService.getEndOverhangChildrenMoveMap(this.replaceBusbarHelper.pendingBusbars, this._moveArticlesMap, insertArticleHelper);

        InsertArticleUtils.mergeMoveMap(this._moveArticlesMap, busbarsEndOverhangChildrenMoveMap);

        this.insertArticleUtils.moveArticlesByMap(this._moveArticlesMap);

        rangeOnLineInfo.updateArticleUsages();
        // connect usages of terminal blocks with mounting rail
        this.connectArticleUsagesWithParent(rangeOnLineInfo.getArticleUsagesOnRail(), insertArticleHelper.acceptingIdentity);

        const articleInfosToCreate: CopiedArticleInformation[] = [];
        const amountOfGroupedArticles: Dictionary<string, number> = this.getAmountOfGroupedArticles(rangeOnLineInfo.getArticlesInformation());
        for (const articleInformation of rangeOnLineInfo.getArticlesInformation().concat()) {
          this.clearUnusedForwardReferenceUsageIds(articleInformation, rangeOnLineInfo);
          if (this.canPasteArticle(articleInformation, rangeOnLineInfo, amountOfGroupedArticles)) {
            articleInfosToCreate.push(articleInformation);
            if (articleInformation instanceof CopiedLabelStripArticleInformation) {
              // remove information about copied label-strip since it will be re-created "from scratch"
              // article-info of the label-strip does not need to be cleaned up
              ArticleUsageUtils.removeAssociationsWithUsages(
                rangeOnLineInfo.getArticleUsages().filter((usage) => usage.id !== articleInformation.articleUsageId),
                articleInformation.articleUsage.id
              );
            }
          } else {
            ArticleUsageUtils.removeAssociationsWithUsages(rangeOnLineInfo.getArticleUsages(), articleInformation.articleUsage.id);
            // remove info because in second paste operation we don't have link on parent and connect labeling without accessories
            rangeOnLineInfo.getArticlesInformation().splice(rangeOnLineInfo.getArticlesInformation().indexOf(articleInformation), 1);
          }
        }

        const staticArticleInfos: CopiedArticleInformation[] = [];
        for (const articleInfo of articleInfosToCreate) {
          if (articleInfo.isDynamic) {
            // collect dynamic articles
            this._dynamicArticleInfos.push(articleInfo);
          } else {
            staticArticleInfos.push(articleInfo);
          }
        }

        if (this._dynamicArticleInfos.length > 0) {
          this._dynamicPasteOperationHelper = new DynamicPasteOperationHelper(
            commonLine.start,
            compositionContainer,
            rangeOnLineInfo,
            this.articleManager,
            this.projectModel,
            this.projectArticleService,
            this.articleContainerLoader,
            this.articleContainerFactory,
            this.replaceLabelingHelper,
            this.compositeArticleUtils,
            this.replaceLabelingUtils,
            this.connectionInfoService,
            this.detachArticleUsageService
          );
        }
        try {
          const createdGuids = await this._staticPasteOperationHelper.executePasteArticles(staticArticleInfos);
          await this.pasteStaticArticlesSuccess(createdGuids);
          return createdGuids;
        } catch (error) {
          Log.error('Could not execute paste articles: ', error);
        }
      } else {
        await this.revertAdditionalUndoOperations();
        throw new Error('copyArticleManager.paste.message.error_free_space');
      }
    } catch (error) {
      Log.error(error)
    }
    return null;
  }

  private canPasteArticle(
    articleInformation: CopiedArticleInformation,
    rangeOnLineInfo: CopiedRangeOnLineArticleInformation,
    amountOfGroupedArticles: Dictionary<string, number>
  ): boolean {
    if (!this.isWireBridgeConnectAvailable(articleInformation, amountOfGroupedArticles)) {
      return false;
    }
    if (articleInformation.requiresAllParentArticlesToBeCopied && !this.isAllParentArticlesCopied(articleInformation, rangeOnLineInfo)) {
      return false;
    }
    return true;
  }

  private updateOffsetPropertyForCoverLabelArticles(articlesInformation: CopiedArticleInformation[], offset: number): void {
    for (const articleInfo of articlesInformation) {
      if (this.articleUsageService.isCoverLabelStripArticleUsage(articleInfo.articleUsage)) {
        ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleInfo.articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET, offset);
      }
    }
  }

  private clearUnusedForwardReferenceUsageIds(articleInfo: CopiedArticleInformation, rangeOnLineInfo: CopiedRangeOnLineArticleInformation): void {
    const forwardReferenceIds: string[] = ReferenceArticleUsageUtils.getForwardReferenceArticleUsageIds(articleInfo.articleUsage);
    for (const forwardId of forwardReferenceIds) {
      if (!rangeOnLineInfo.getArticleInfoByArticleUsageId(forwardId)) {
        ReferenceArticleUsageUtils.removeForwardReferenceArticleUsageId(articleInfo.articleUsage, forwardId);
      }
    }
  }

  private async removeLabelingArticles(labelStripsToDetach: ArticleContainer[]): Promise<void> {
    const detachOperation: IOperation = await this.replaceLabelingHelper.detachLabelStrips(labelStripsToDetach);
    if (detachOperation) {
      this._additionalUndoOperations.push(detachOperation);
    }
  }

  private async pasteStaticArticlesSuccess(createdContainerGUIDs: string[]): Promise<void> {
    if (ArrayUtils.isNotEmpty(createdContainerGUIDs)) {
      this._createdContainerGUIDs = this._createdContainerGUIDs.concat(createdContainerGUIDs);
    }
    await this.createDynamicArticles();
  }

  private async pasteDynamicArticlesSuccess(createdContainerGUIDs: string[]): Promise<void> {
    if (ArrayUtils.isNotEmpty(createdContainerGUIDs)) {
      this._createdContainerGUIDs = this._createdContainerGUIDs.concat(createdContainerGUIDs);
    }
    await this.finalizeArticlesCreation();
  }

  private async createDynamicArticles(): Promise<void> {
    if (this._dynamicPasteOperationHelper) {
      let dynamicArticleInfos: CopiedArticleInformation[] = [];
      if (ArrayUtils.isNotEmpty(this._dynamicArticleInfos)) {
        dynamicArticleInfos = this._dynamicArticleInfos.concat();
        this._dynamicArticleInfos = [];
      }
      try {
        const createdGuids = await this._dynamicPasteOperationHelper.executePasteArticles(dynamicArticleInfos);
        await this.pasteDynamicArticlesSuccess(createdGuids);
      } catch (error) {
        Log.error('Could not execute paste articles: ', error);
      }
    } else {
      await this.finalizeArticlesCreation();
    }
  }

  private async finalizeArticlesCreation(): Promise<void> {
    this.wireBridgeService.createJumpersBetweenArticles(this.containerService.findArticleContainersInGlobalByGuids(this._createdContainerGUIDs));
  }

  private getAmountOfGroupedArticles(articlesInformation): Dictionary<string, number> {
    const amountOfGroupedArticles: Dictionary<string, number> = new Dictionary<string, number>();
    let articleUsageGroupId: string = null;
    let amountArticleFromGroup: number = 0;
    for (const articleInfo of articlesInformation) {
      articleUsageGroupId = articleInfo.articleUsageGroupId;
      if (articleUsageGroupId) {
        amountArticleFromGroup = amountOfGroupedArticles.getValue(articleUsageGroupId);
        if (!amountArticleFromGroup) {
          amountArticleFromGroup = 1;
        } else {
          amountArticleFromGroup++;
        }
        amountOfGroupedArticles.setValue(articleUsageGroupId, amountArticleFromGroup);
      }
    }
    return amountOfGroupedArticles;
  }

  private isAllParentArticlesCopied(articleInfo: CopiedArticleInformation, rangeOnLineInfo: CopiedRangeOnLineArticleInformation): Boolean {
    if (rangeOnLineInfo.isArticleConnectedToRangeOnLine(articleInfo.articleUsageId)) {
      return true;
    }

    const parentUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleInfo.articleUsage);
    const backwardReferenceUsageIds: string[] = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageIds(articleInfo.articleUsage);
    ArrayUtils.mergeArrays(parentUsageIds, backwardReferenceUsageIds);
    if (parentUsageIds.length === 0) {
      return false;
    }
    for (const parentId of parentUsageIds) {
      if (!rangeOnLineInfo.getArticleInfoByArticleUsageId(parentId)) {
        return false;
      }
    }
    return true;
  }

  private connectArticleUsagesWithParent(articleUsages: ArticleUsageVO[], parentAcceptingIdentity: MetaElementUsageIdentityVO): void {
    const parentArticle: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentAcceptingIdentity.articleUsageId);
    let acceptingUsage = ArticleUsageUtils.findConnectionAreaUsageByName(parentArticle.articleUsage, parentAcceptingIdentity.metaElementName);
    for (const articleUsage of articleUsages) {
      const givingConnectionUsages: ConnectionAreaUsageVO[] = ArticleUsageUtils.backwardConnectionAreaUsages(articleUsage);
      for (const givingUsage of givingConnectionUsages) {
        if (!acceptingUsage) {
          acceptingUsage = this.projectMetaElementService.addMetaElementUsage(
            parentAcceptingIdentity.articleUsageId,
            parentArticle.findMetaElementByName(parentAcceptingIdentity.metaElementName)
          ) as ConnectionAreaUsageVO;
        }
        ConnectionAreaUsageUtils.connect(acceptingUsage, givingUsage);
      }
    }
  }

  private isWireBridgeConnectAvailable(articleInformation: CopiedArticleInformation, amountOfGroupedArticles: Dictionary<string, number>): Boolean {
    const articleUsageGroupId: string = articleInformation.articleUsageGroupId;
    if (!articleUsageGroupId) {
      return true;
    }

    const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleInformation.articleUsage);
    if (!ArticleUtils.isWireBridgeArticle(article)) {
      return true;
    }

    const multiplyRange: IntRangeVO = ArticleUtils.getWireBridgeMultiplyRange(article);
    const copiedWireBridgesAmount: number = amountOfGroupedArticles.getValue(articleUsageGroupId);
    return copiedWireBridgesAmount >= multiplyRange.left && copiedWireBridgesAmount <= multiplyRange.right;
  }

  private async revertAdditionalUndoOperations(): Promise<void> {
    if (ArrayUtils.isEmpty(this._additionalUndoOperations)) {
      return;
    }
    for (const additionalUndoOperation of this._additionalUndoOperations) {
      await additionalUndoOperation.performUndo();
    }
    this._additionalUndoOperations = [];
  }

  public get additionalArticlesOperations(): IOperation[] {
    return this._additionalUndoOperations;
  }

  public get moveMap(): Dictionary<string, Vector3> {
    return this._moveArticlesMap;
  }

  public clear(): void {
    if (this._staticPasteOperationHelper) {
      this._staticPasteOperationHelper.clear();
      this._staticPasteOperationHelper = null;
    }
    if (this._dynamicPasteOperationHelper) {
      this._dynamicPasteOperationHelper.clear();
      this._dynamicPasteOperationHelper = null;
    }
    this._createdContainerGUIDs = [];
    this._dynamicArticleInfos = [];

    this._moveArticlesMap = null;

    this._additionalUndoOperations = [];
  }
}
